/**
 * Mini-player persistente dentro de la conversación (Fase 19.D). Portado de
 * `listen-screens.jsx` (ListenMiniPlayer). Barra compacta que aparece cuando la
 * charla se abrió desde "Escuchar" (la monta `ConversationMobile`). Comparte el
 * `<audio>` ÚNICO vía `useListen()` — NO crea otro elemento. `onExpand` vuelve al
 * player. Timing REAL: `pos`/`duration` del store.
 */

import { useListen } from './ListenProvider.js';
import { BookCover } from './BookCover.js';
import { LIAlert, LIPlay, LIPause, LIExpand, LIX } from './listenIcons.js';
import { fmtClock } from './listenUi.js';
// El mini-player se monta GLOBALMENTE (GlobalListenBar en /activity, /program…),
// donde ninguna pantalla de listen cargó listen.css. Lo importa él mismo para que
// sus estilos (.mp-*) existan siempre, no sólo dentro del feature Escuchar.
import './listen.css';

interface ListenMiniPlayerProps {
  /** Expandir → volver al player (`navigate('/listen/player')`). */
  onExpand: () => void;
  /** Cerrar el mini-player y seguir en la charla (pausa el audio). */
  onClose?: () => void;
}

export function ListenMiniPlayer({ onExpand, onClose }: ListenMiniPlayerProps) {
  const L = useListen();
  if (!L.isOpen || !L.book || !L.chapter) return null;

  const { book, chapter, pos, duration, playing } = L;
  const frac = duration > 0 ? Math.min(1, pos / duration) : 0;

  if (L.error) {
    return (
      <div className="mp-bar is-error" role="status">
        <span className="mp-ic">
          <LIAlert size={16} />
        </span>
        <div className="mp-meta">
          <div className="mp-kick">No se pudo cargar</div>
          <div className="mp-title">{L.error}</div>
        </div>
        {onClose && (
          <button
            type="button"
            className="mp-expand"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Cerrar el reproductor"
          >
            <LIX size={14} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className="mp-bar"
      role="button"
      tabIndex={0}
      onClick={onExpand}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onExpand();
        }
      }}
    >
      <BookCover book={book} className="mp-cover" titleSize={9} pad={5} />
      <div className="mp-meta">
        <div className="mp-kick">
          {L.loading
            ? 'Cargando el capítulo…'
            : `Escuchando · ${fmtClock(pos)} / ${fmtClock(duration)}`}
        </div>
        <div className="mp-title">{chapter.title}</div>
        <div className={`mp-scrub${L.loading ? ' is-loading' : ''}`}>
          <span style={L.loading ? undefined : { width: `${frac * 100}%` }} />
        </div>
      </div>
      <button
        type="button"
        className="mp-play"
        onClick={(e) => {
          e.stopPropagation();
          L.toggle();
        }}
        disabled={L.loading}
        aria-label={L.loading ? 'Cargando el capítulo' : playing ? 'Pausar' : 'Reproducir'}
      >
        {L.loading ? (
          <span className="mp-spin" />
        ) : playing ? (
          <LIPause size={16} />
        ) : (
          <LIPlay size={16} />
        )}
      </button>
      <button
        type="button"
        className="mp-expand"
        onClick={(e) => {
          e.stopPropagation();
          onExpand();
        }}
        aria-label="Volver al reproductor"
      >
        <LIExpand size={15} />
      </button>
      {onClose && (
        <button
          type="button"
          className="mp-expand"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Cerrar el reproductor"
        >
          <LIX size={14} />
        </button>
      )}
    </div>
  );
}
